/**
 * The inbox query cache shapes and the pure merge rules used by polling (PROD-1849).
 *
 * POLLING MERGE (documented):
 *   - The first read of a list or a thread is a normal page (no `updatedAfter`).
 *   - Every later poll sends `updatedAfter` = the cache's `syncedAt` MINUS a
 *     look-back (`POLL_LOOKBACK_MS`). `syncedAt` is the latest `X-Server-Time` seen
 *     for that query. When the header is missing (not sent, or not exposed to the
 *     browser by CORS) the fallback is the newest `lastMessageAt` / `sentAt` seen.
 *     The look-back is what makes polling reliable: a message stored with a time a
 *     little before the last poll (the WhatsApp send time, a slow webhook, two
 *     clocks) is still returned. The merge is by id, so the overlap is harmless.
 *   - The poll returns only changed items. They are merged by `id`: a known id is
 *     replaced in place (so a `deliveryStatus` or `unreadCount` change updates the
 *     row), a new id is added, nothing is removed. The result is re-sorted.
 *   - If one poll returns more than a page (`nextCursor` not null), the merge is
 *     not trusted and the query reloads its first page instead.
 *   - A merge always runs against the LATEST cache (read after the request
 *     returns), so an optimistic reply bubble or an "older messages" page that
 *     arrived during the request is never dropped.
 */
import type { Conversation, Message } from '../inbox-types';

export interface ConversationListData {
  /** Sorted by lastMessageAt desc, then id (the server's order). */
  items: Conversation[];
  /** The first page's cursor to the next page (null when everything fit). */
  nextCursor: string | null;
  /** `updatedAfter` for the next poll. Null until anything was seen. */
  syncedAt: string | null;
}

export interface ThreadData {
  /** Oldest first, for display. */
  messages: Message[];
  /** Cursor to OLDER messages (null when the oldest is loaded). */
  olderCursor: string | null;
  syncedAt: string | null;
}

/** The id prefix of an optimistic staff reply still waiting for its 202. */
export const PENDING_PREFIX = 'pending:';

export const pendingId = (clientMessageId: string) => `${PENDING_PREFIX}${clientMessageId}`;
export const isPending = (m: Message) => m.id.startsWith(PENDING_PREFIX);

function compareConversations(a: Conversation, b: Conversation): number {
  const byTime = b.lastMessageAt.localeCompare(a.lastMessageAt);
  return byTime !== 0 ? byTime : a.id.localeCompare(b.id);
}

function compareMessages(a: Message, b: Message): number {
  const byTime = a.sentAt.localeCompare(b.sentAt);
  return byTime !== 0 ? byTime : a.id.localeCompare(b.id);
}

/** Upsert by id: a known id is replaced in place, a new id is added. */
function upsert<T extends { id: string }>(prev: readonly T[], incoming: readonly T[]): T[] {
  const byId = new Map<string, T>();
  for (const item of prev) byId.set(item.id, item);
  for (const item of incoming) if (item.id) byId.set(item.id, item);
  return [...byId.values()];
}

export function mergeConversations(prev: readonly Conversation[], changed: readonly Conversation[]): Conversation[] {
  return upsert(prev, changed).sort(compareConversations);
}

/** Merges messages (any order in) into an oldest-first thread. */
export function mergeMessages(prev: readonly Message[], incoming: readonly Message[]): Message[] {
  return upsert(prev, incoming).sort(compareMessages);
}

/** How far each poll looks back before `syncedAt` (see the header). */
export const POLL_LOOKBACK_MS = 5 * 60_000;

/** The `updatedAfter` a poll sends: `syncedAt` minus the look-back (unchanged when unparseable). */
export function pollSince(syncedAt: string): string {
  const ms = Date.parse(syncedAt);
  return Number.isFinite(ms) ? new Date(ms - POLL_LOOKBACK_MS).toISOString() : syncedAt;
}

/**
 * The next `updatedAfter`: the server clock when the response carried it; otherwise
 * the newest item time seen (never earlier than the previous value).
 */
export function nextSyncedAt(prev: string | null, serverTime: string | null, itemTimes: readonly string[]): string | null {
  if (serverTime) return serverTime;
  let best = prev;
  for (const t of itemTimes) {
    if (!Number.isFinite(Date.parse(t))) continue;
    if (best === null || Date.parse(t) > Date.parse(best)) best = t;
  }
  return best;
}

/** Replaces the optimistic bubble of `clientMessageId` with the server's message (dedupe by id). */
export function settlePending(thread: ThreadData, clientMessageId: string, server: Message): ThreadData {
  const rest = thread.messages.filter((m) => m.id !== pendingId(clientMessageId));
  return { ...thread, messages: server.id ? mergeMessages(rest, [server]) : rest };
}

/** Drops the optimistic bubble of `clientMessageId` (the send failed). */
export function dropPending(thread: ThreadData, clientMessageId: string): ThreadData {
  return { ...thread, messages: thread.messages.filter((m) => m.id !== pendingId(clientMessageId)) };
}
